import { Table as BaseTable, TableProps } from '@/components/table'
import { Query } from './table_query'
import { ClientTypesQuery } from './__generated__/table_query'
import { useQuery } from '@apollo/client'
import { Inline as Error } from '@/components/error'
import { Inline as Loading } from '@/components/loading'
import { ClientTypeFilters, ClientTypeOrder, SortOrder } from '@/types'
import { useTable } from '@/helpers/useTable'
import { dedupe } from '@/helpers/dedupe'
import { Button } from '../base/button'
import { ShowDialog } from './show_dialog'
import { NewDialog } from './new_dialog'
import { Badge } from '@/components/client_type/badge'

interface Props extends TableProps {
  filters?: ClientTypeFilters
  createable?: boolean
}

export const Table = ({ pageSize = 20, filters = {}, onIndex = false, createable = false, ...props }: Props) => {
  const { data, loading, error, refetch, fetchMore } = useQuery<ClientTypesQuery>(
    Query,
    { variables: { filters, first: pageSize } }
  )
  const { pageInfo, openNew, setOpenNew, openShow, showShow, hideShow, resourceId } = useTable({ pageInfo: data?.clientTypes.pageInfo, onIndex, basePath: '/settings/client-types' })

  if (error) { return <Error message={error.message} /> }
  if (loading || !data) { return <Loading /> }

  return (
    <>
      <BaseTable<ClientTypeFilters, ClientTypeOrder>
        {...props}
        actionButton={createable ? <Button color="green" onClick={() => setOpenNew(true)}>Add</Button> : undefined}
        defaultSort={{ name: SortOrder.Asc }}
        headings={[{ name: 'Name', sortField: 'name', defaultDirection: SortOrder.Asc }]}
        initialFilters={filters}
        moreToFetch={pageInfo.current?.hasNextPage}
        refetch={refetch}
        fetchMore={() => fetchMore({
          variables: { after: pageInfo.current?.endCursor },
          updateQuery(previousData, { fetchMoreResult }) {
            if (Object.keys(previousData).length === 0) return fetchMoreResult
            fetchMoreResult.clientTypes.nodes = dedupe([
              ...previousData.clientTypes.nodes,
              ...fetchMoreResult.clientTypes.nodes
            ])
            return { ...fetchMoreResult }
          }
        })}
        filters={[
          {
            index: 0,
            setValue: (set, value) => set(f => ({ ...f, nameCont: value })),
            type: 'text',
            placeholder: 'Now, what did we call it?'
          },
        ]}
        rows={data.clientTypes.nodes.map(clientType => (
          {
            id: clientType.id,
            columns: [
              <Badge clientType={clientType} />
            ]
          }
        ))}
        onRowClick={showShow}
      />
      { resourceId && <ShowDialog id={resourceId} open={openShow} setOpen={hideShow} /> }
      <NewDialog open={openNew} setOpen={setOpenNew} />
    </>
  )
}
